/**
 * GA4 E-Commerce DataLayer Tracking
 * Pushes GA4 e-commerce events to window.dataLayer for GTM
 */

import type { Product, CartLine, Cart } from '~/lib/shopify/types';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  index?: number;
}

function pushEvent(event: string, ecommerce: Record<string, unknown>) {
  window.dataLayer = window.dataLayer || [];
  // Clear previous ecommerce data per GA4 best practice
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce });
}

function productToItem(product: Product, index?: number): GA4Item {
  const variant = product.variants.edges[0]?.node;
  const item: GA4Item = {
    item_id: variant?.sku || product.id,
    item_name: product.title,
    item_brand: product.vendor || undefined,
    item_category: product.productType || undefined,
    price: parseFloat(product.priceRange.minVariantPrice.amount),
  };
  if (index !== undefined) item.index = index;
  return item;
}

function cartLineToItem(line: CartLine): GA4Item {
  return {
    item_id: line.merchandise.id,
    item_name: line.merchandise.product.title,
    item_brand: line.merchandise.product.vendor || undefined,
    item_variant: line.merchandise.title !== 'Default Title' ? line.merchandise.title : undefined,
    price: parseFloat(line.merchandise.price.amount),
    quantity: line.quantity,
  };
}

export function trackViewItem(product: Product) {
  pushEvent('view_item', {
    currency: product.priceRange.minVariantPrice.currencyCode,
    value: parseFloat(product.priceRange.minVariantPrice.amount),
    items: [productToItem(product)],
  });
}

export function trackViewItemList(products: Product[], listName: string) {
  if (products.length === 0) return;
  pushEvent('view_item_list', {
    item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
    item_list_name: listName,
    items: products.map((p, i) => productToItem(p, i)),
  });
}

export function trackAddToCart(line: CartLine) {
  pushEvent('add_to_cart', {
    currency: line.merchandise.price.currencyCode,
    value: parseFloat(line.cost.totalAmount.amount),
    items: [cartLineToItem(line)],
  });
}

export function trackRemoveFromCart(line: CartLine) {
  pushEvent('remove_from_cart', {
    currency: line.merchandise.price.currencyCode,
    value: parseFloat(line.cost.totalAmount.amount),
    items: [cartLineToItem(line)],
  });
}

export function trackViewCart(cart: Cart) {
  const items = cart.lines.edges.map((e) => cartLineToItem(e.node));
  pushEvent('view_cart', {
    currency: cart.cost.totalAmount.currencyCode,
    value: parseFloat(cart.cost.totalAmount.amount),
    items,
  });
}

export function trackBeginCheckout(cart: Cart) {
  const items = cart.lines.edges.map((e) => cartLineToItem(e.node));
  pushEvent('begin_checkout', {
    currency: cart.cost.totalAmount.currencyCode,
    value: parseFloat(cart.cost.totalAmount.amount),
    items,
  });
}

export function trackSelectItem(product: Product, index: number, listName: string) {
  pushEvent('select_item', {
    item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
    item_list_name: listName,
    items: [productToItem(product, index)],
  });
}

export function trackSearch(searchTerm: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'search',
    search_term: searchTerm,
  });
}
